// Pre-move on/off preference (Settings toggle). A pre-move — queueing your reply
// during the opponent's turn so it fires the instant they move — is ON by default
// (its long-standing behaviour); this lets a player who finds it error-prone turn
// it off. Client-only, persisted in localStorage, and mirrored across an open
// board via a `premovechange` event, mirroring `theme.ts` / `sound.ts`.

import { t } from "../i18n/index.ts";
import { listenWhileMounted } from "../ui/lifecycle.ts";

const STORAGE_KEY = "chess:premove";
const CHANGE_EVENT = "premovechange";
let memory: boolean | undefined;

/** Whether pre-move is enabled. Defaults to ON — including when storage is
 *  unavailable — so behaviour only changes for someone who explicitly opts out. */
export function isPremoveEnabled(): boolean {
  if (memory !== undefined) return memory;
  try {
    return localStorage.getItem(STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Persist + broadcast a pre-move on/off choice. */
export function setPremoveEnabled(on: boolean): void {
  memory = on;
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    /* non-fatal: the toggle just won't persist across reloads */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: on }));
  }
}

/** Subscribe to pre-move on/off changes; returns an unsubscribe function. Lets a
 *  live board react immediately when the setting is toggled in another view. */
export function onPremoveChange(cb: (on: boolean) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb(isPremoveEnabled());
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** A labelled checkbox for the Settings panel (kept in sync via `premovechange`). */
export function premoveToggle(): HTMLElement {
  const field = document.createElement("label");
  field.className = "field field-toggle";
  field.innerHTML =
    `<span class="field-toggle-text">${t("premove.label")}` +
    `<small class="fine">${t("premove.help")}</small>` +
    `</span>` +
    `<input type="checkbox" name="premove" />`;
  const input = field.querySelector<HTMLInputElement>("input")!;
  input.checked = isPremoveEnabled();
  input.addEventListener("change", () => setPremoveEnabled(input.checked));
  listenWhileMounted(field, CHANGE_EVENT, () => { input.checked = isPremoveEnabled(); });
  return field;
}
